/**
 * The evidence "Ask Iris" sends from the BI dashboards (CU-869fkdhjv phase 3, wave 2, plan § 14): Season vs last year,
 * Price curve of a date and My office network. Each builder takes what the component already paints — the cell's
 * model, the lines left visible, the card's rows — and returns `{ kind, labels, subject?, state }`: the question's
 * kind and labels (`@utils/askIris/questions`) and the `canvas_state` (`@utils/askIris/canvasState`). Nothing is
 * fetched and nothing is recomputed that the screen did not compute (criterion 6): what a dialog derives on its own
 * (Season's "Who moved most") comes in as the dialog computed it.
 *
 * Labels are the screen's (office, provider, dates, length), never an id (criterion 28); the Cars and market texts go
 * only when they are on, as the page's context line says them (UX C12). Ids live in `params.ids`.
 *
 * No DS Repricing data exists on these screens (criteria 21-26): nothing of the engine is ever put here.
 *
 * Pure and alias-free (TL R4): relative imports only, and nothing from one dashboard's folder (`importDirection`).
 */
import { canvasState, captureAge } from '../../../../@utils/askIris/canvasState.js'
import { addDays, fromIso, toIso } from './dates.js'

export const VIEWS = {
	season: 'bi.seasonVsLastYear',
	priceCurve: 'bi.priceCurveOfADate',
	network: 'bi.myOfficeNetwork',
}
export const SCREENS = {
	season: 'Season vs last year',
	priceCurve: 'Price curve of a date',
	network: 'My office network',
}

const MAX_LINES = 8
const MAX_ROWS = 6
const MAX_GROUP_COLUMNS = 8
const STAT = { min: 'Min', avg: 'Avg', max: 'Max' }
const COMPARE = { ly: 'last year', lw: 'a week before', yday: 'the day before' }

const isNum = v => typeof v === 'number' && Number.isFinite(v)
const r2 = v => (isNum(v) ? Math.round(v * 100) / 100 : null)
const pct1 = ratio => (isNum(ratio) ? Math.round(ratio * 1000) / 10 : null)
const pts1 = v => (isNum(v) ? Math.round(v * 10) / 10 : null)

// drop null / undefined / '' / empty arrays and objects, so the evidence carries only what exists
function compact(obj) {
	const out = {}
	for (const [k, v] of Object.entries(obj || {})) {
		if (v == null || v === '') continue
		if (Array.isArray(v) && !v.length) continue
		if (!Array.isArray(v) && typeof v === 'object' && !Object.keys(v).length) continue
		out[k] = v
	}
	return out
}

/** A pick-up label from ISO dates: one date, or a week as its range */
export const pickupLabel = (dates = []) => {
	const ds = (dates || []).filter(Boolean)
	if (!ds.length) return null
	return ds.length === 1 ? { date: ds[0] } : { range: [ds[0], ds[ds.length - 1]] }
}

/** The labels every element of a view shares, after the element's own: office, pick-up, length, cars and market when on */
function contextLabels({ officeName, dates, lor, carsText, marketText } = {}) {
	return [officeName || null, pickupLabel(dates), lor != null ? { days: lor } : null, carsText || null, marketText || null]
}

/** Cars and the market filter, named only when on (UX C12): the caller's texts, else what the scope carries */
const namedFilters = ({ carsText, marketText, scope } = {}) => [carsText ?? scope?.params?.scope?.cars ?? null, marketText ?? scope?.params?.scope?.market ?? null]

// ---------------------------------------------------------------------------
// Scope and freshness
// ---------------------------------------------------------------------------

/**
 * `{ params, freshness }` of a BI view. `params.scope` is what the context line says (labels), `params.ids` the
 * request's ids (office, cars, market providers); `freshness` is the last capture day shown.
 */
export function biScope({
	officeName, officeId, offices, lor, lengths, stat, pickups, cmp, same, compareText, carsText, carsIds, marketText,
	marketIds, code, perDay, captured, capture, now = new Date(),
} = {}) {
	const compare = cmp ? (cmp === 'ly' ? `same ${same ? 'date' : 'weekday'} last year` : COMPARE[cmp] || cmp) : null
	const params = compact({
		scope: compact({
			office: officeName || null,
			offices: offices ?? null,
			length_days: lor ?? null,
			lengths: lengths && lengths.length ? lengths : null,
			stat: STAT[stat] || null,
			pickups: pickups && pickups.length ? (pickups.length === 1 ? pickups[0] : { from: pickups[0], to: pickups[pickups.length - 1] }) : null,
			compare: compareText || compare,
			cars: carsText || null,
			market: marketText || null,
			currency: code || null,
			price: perDay == null ? null : (perDay ? 'daily' : 'total'),
			captured: captured || null,
		}),
		ids: compact({
			office_id: officeId ?? null,
			cars_ids: carsIds && carsIds.length ? carsIds : null,
			market_provider_ids: marketIds && [...marketIds].length ? [...marketIds] : null,
		}),
	})
	return { params, freshness: compact({ data_source: 'live', captured_until: capture || null, capture_age: capture ? captureAge(capture, now) : null }) }
}

/** My office network: the first day of the comparison period, as its "Compare with" field says it (`compareUnder`) */
export function networkSinceIso(cmp, captureFrom) {
	const from = captureFrom instanceof Date ? captureFrom : fromIso(captureFrom)
	if (!from) return null
	if (cmp === 'day') return toIso(addDays(from, -1))
	if (cmp === 'week') return toIso(addDays(from, -7))
	if (cmp === 'year') return toIso(addDays(from, -364))
	return toIso(new Date(from.getFullYear() - 1, from.getMonth(), from.getDate()))
}

const state = ({ view, selection, digest, scope, definition }) => canvasState({
	view: VIEWS[view], screen: SCREENS[view], selection, digest, params: scope?.params || {}, freshness: scope?.freshness || {}, definition,
})

// ---------------------------------------------------------------------------
// Season vs last year
// ---------------------------------------------------------------------------

const period = stats => (stats ? compact({ prices: stats.n ?? null, captured: stats.firstCapture && stats.lastCapture ? [stats.firstCapture, stats.lastCapture] : null }) : null)
const mover = m => (m ? compact({ provider: m.provider?.name, change: r2(m.e), change_pct: pct1(m.p), few_prices: m.thin || null }) : null)

/**
 * The detail of a Season cell (provider or Market median), with the dialog's props: `target` `{ rowKey, j }`,
 * `model` (`buildModel`), `view` (`cmp`, `same`, `stat`, `lor`, `by`), `moved` = the dialog's "Who moved most"
 * (`movers(model.rows, j)`), and the page's texts.
 */
export function seasonCellEvidence({ target, model, view = {}, moved = {}, code, officeName, carsText, marketText, isMedian, scope }) {
	if (!target || !model) return null
	const { rowKey, j } = target
	const median = isMedian ?? rowKey === '__median'
	const row = median ? null : (model.rows || []).find(r => r.provider.id === rowKey)
	const cell = median ? model.median?.[j] : row?.cells?.[j]
	if (!cell?.cur) return null
	const column = model.columns[j] || {}
	const name = median ? 'Market median' : row.provider.name
	const isWeek = view.by === 'week' || (column.dates || []).length > 1
	const cur = cell.cur, prev = cell.prev
	const stand = model.stand?.[j]
	const you = model.you?.cells?.[j]

	const mmm = key => compact({ now: r2(cur[key]), before: prev ? r2(prev[key]) : null, change: prev && isNum(cur[key]) && isNum(prev[key]) ? r2(cur[key] - prev[key]) : null })
	const selection = compact({
		provider: name,
		market_median: median || null,
		own: row?.provider?.own || null,
		pickup: isWeek ? compact({ week: String(column.key ?? '').slice(4) || null, from: column.dates?.[0], to: column.dates?.at(-1) }) : column.dates?.[0],
		stat: STAT[view.stat] || null,
		value: r2(cell.val),
		previous_value: prev ? r2(cell.prevVal) : null,
		change: prev ? r2(cell.de) : null,
		change_pct: prev ? pct1(cell.dp) : null,
		currency: code || null,
		compare: view.cmp === 'ly' ? `same ${view.same ? 'date' : 'weekday'} last year` : COMPARE[view.cmp] || null,
		now: period(cur),
		before: period(prev),
		previous_pickups: (column.prevDates || []).length ? [column.prevDates[0], column.prevDates.at(-1)] : null,
		few_prices_now: cur.n < 5 || null,
		few_prices_before: (prev && prev.n < 5) || null,
		no_comparison: prev ? null : (cell.why === 'passed' ? 'pick-up already gone by in the comparison period' : 'no prices captured in the comparison period'),
	})
	const digest = compact({
		prices_behind: { min: mmm('min'), median: mmm('median'), max: mmm('max') },
		biggest_drop: mover(moved?.drop),
		biggest_rise: mover(moved?.rise),
		column: compact({
			cheapest: stand?.cheapest ? { provider: stand.cheapest.row.provider.name, price: r2(stand.cheapest.v) } : null,
			market_median: r2(model.median?.[j]?.val),
			your_row: model.you && you?.cur ? { provider: model.you.provider.name, price: r2(you.val), position: stand?.pos ? `${stand.pos} of ${stand.total}` : null } : null,
		}),
		among_shown: model.filtered ? 'computed among the providers shown (Tier / Providers filter on)' : null,
	})
	return {
		kind: 'change',
		labels: [name, ...contextLabels({ officeName, dates: column.dates, lor: view.lor, carsText, marketText }), view.cmp ? { compare: view.cmp } : null],
		state: state({ view: 'season', selection, digest, scope, definition: 'bi.seasonCell' }),
	}
}

const byD = points => [...(points || [])].sort((a, b) => b.d - a.d)

/** One line of a curve as Iris reads it: first and last capture (days before pick-up), min, max, thin days */
export function lineSummary(points) {
	const ps = byD(points)
	if (!ps.length) return null
	const values = ps.map(p => p.value)
	const first = ps[0], last = ps[ps.length - 1]
	return compact({
		first: { days_before: first.d, price: r2(first.value) },
		last: { days_before: last.d, price: r2(last.value) },
		min: r2(Math.min(...values)),
		max: r2(Math.max(...values)),
		few_prices_days: ps.filter(p => p.thin).length || null,
	})
}

const lineOf = (line, { prev = false } = {}) => compact({
	name: line.name,
	tag: line.tag || null,
	...(lineSummary(line.points) || { no_prices: true }),
	last_year: prev && line.prev?.length ? lineSummary(line.prev) : null,
})

/**
 * The curve inside a Season cell (`Dashboards/@components/PriceCurve`): only the lines the user left visible, last
 * year only with its switch on, the other competitors only with theirs.
 */
export function seasonCurveEvidence({ curve, hidden = {}, showPrev = false, showOthers = false, name, officeName, lor, stat, carsText, marketText, code, scope }) {
	if (!curve || curve.empty) return null
	const main = (curve.main || []).filter(s => !hidden[s.key])
	const others = (curve.others || []).filter(s => !hidden[s.key])
	const lines = [...main.map(s => lineOf(s, { prev: showPrev })), ...(showOthers ? others.map(s => lineOf(s)) : [])].slice(0, MAX_LINES)
	if (!lines.length) return null
	const selection = compact({
		element: 'price curve before pick-up',
		provider: name || null,
		pickups: curve.pickupDates?.length ? curve.pickupDates : null,
		stat: STAT[stat] || null,
		currency: code || null,
		days_shown: curve.days || null,
		last_year_shown: (curve.hasPrev && showPrev) || null,
		other_competitors: (curve.others || []).length ? compact({ count: curve.others.length, shown: showOthers }) : null,
	})
	const digest = compact({ lines, hidden: (curve.main || []).filter(s => hidden[s.key]).map(s => s.name) })
	return {
		kind: 'curve',
		labels: [name || null, ...contextLabels({ officeName, dates: curve.pickupDates, lor, carsText, marketText })],
		state: state({ view: 'season', selection, digest, scope, definition: 'bi.seasonCurve' }),
	}
}

// ---------------------------------------------------------------------------
// Price curve of a date
// ---------------------------------------------------------------------------

/**
 * The curve of "Price curve of a date" (`DateCurve`): the visible lines (You and Market median first), last year for
 * You, the median and the highlighted line when "Last year" is on, the first move, the not-captured zone.
 */
export function dateCurveEvidence({ curve, hidden = {}, ly = false, highlight = null, firstMove = null, zone = null, officeName, lor, stat, carsText, marketText, code, scope }) {
	if (!curve || curve.beyond || curve.empty) return null
	const visible = (curve.lines || []).filter(l => !hidden[l.key])
	if (!visible.length) return null
	const prevOf = l => ly && (l.role !== 'other' || l.key === highlight)
	const lines = visible.slice(0, MAX_LINES).map(l => lineOf(l, { prev: prevOf(l) }))
	const moverLine = firstMove ? (curve.lines || []).find(l => l.key === firstMove.key) : null
	const selection = compact({
		element: 'price curve before pick-up',
		pickups: curve.pickupDates?.length ? curve.pickupDates : null,
		stat: STAT[stat] || null,
		currency: code || null,
		days_shown: curve.days || null,
		lines_shown: `${visible.length} of ${(curve.lines || []).length}`,
		last_year_shown: (curve.hasPrev && ly) || null,
		highlighted: highlight ? (curve.lines || []).find(l => l.key === highlight)?.name : null,
	})
	const digest = compact({
		first_move: moverLine ? compact({ provider: moverLine.name, days_before: firstMove.d, change_pct: pct1(firstMove.pct) }) : null,
		not_captured_yet: zone ? `the last ${zone.from} days before pick-up are not captured yet` : null,
		lines,
		lines_cut: visible.length > MAX_LINES ? visible.length - MAX_LINES : null,
	})
	return {
		kind: 'curve',
		labels: contextLabels({ officeName, dates: curve.pickupDates, lor, carsText, marketText }),
		state: state({ view: 'priceCurve', selection, digest, scope, definition: 'bi.dateCurve' }),
	}
}

const changeRow = r => compact({ provider: r.line?.name, tag: r.line?.tag || null, change: r2(r.eur), change_pct: pct1(r.pct), few_prices: r.thin || null })

/**
 * The three cards of Price curve. `card` = `last7` (`rows` as the card shows them), `whoMovedFirst` (`moved` as the
 * card shows it), `position` (`pos`; `null` without identity: that card only says "Set your brand…").
 */
export function cardEvidence({ card, title, rows = [], moved = null, pos = null, identity = true, curve, officeName, lor, carsText, marketText, code, scope }) {
	if (!curve || curve.beyond || curve.empty) return null
	const ctx = contextLabels({ officeName, dates: curve.pickupDates, lor, carsText, marketText })
	let selection, digest, labels = ctx, definition
	if (card === 'last7') {
		definition = 'bi.last7'
		selection = { card: title }
		const shown = rows.filter(r => !r.none)
		digest = compact({
			rows: shown.slice(0, MAX_ROWS).map(changeRow),
			without_comparison: rows.filter(r => r.none).map(r => r.line?.name).slice(0, MAX_ROWS),
		})
	} else if (card === 'whoMovedFirst') {
		definition = 'bi.whoMovedFirst'
		if (!moved) return null
		const nobody = !moved.first?.length
		selection = compact({ card: title, nobody_moved_5_pct: nobody || null })
		const move = m => compact({ ...changeRow(m), days_before: m.d })
		digest = nobody
			? compact({ biggest_daily_move: moved.biggest ? move(moved.biggest) : null })
			: compact({
				first: moved.first.slice(0, 2).map(move),
				followers: moved.followers.slice(0, MAX_ROWS - Math.min(2, moved.first.length)).map(move),
				followers_total: moved.followers.length,
			})
	} else if (card === 'position') {
		definition = 'bi.position'
		if (!identity || !pos) return null
		if (pos.rank) labels = [{ rank: [pos.rank, pos.of] }, ...ctx]
		selection = compact({
			card: title,
			days_before: pos.d ?? null,
			rank: pos.rank ? `${pos.rank} of ${pos.of}` : null,
			your_price: r2(pos.you),
			currency: code || null,
			no_price_of_your_brand: pos.none || null,
			no_competitor_with_a_price: pos.alone || null,
			few_prices: pos.thin || null,
		})
		digest = pos.gap ? compact({
			gap: compact({ to: pos.gap.name, who: pos.gap.kind === 'below' ? 'the second' : 'the cheapest', amount: r2(pos.gap.eur), pct: pct1(pos.gap.pct), you_are: pos.gap.kind }),
			market_median: r2(pos.median),
			providers_without_a_price: pos.noPrice || null,
		}) : {}
	} else {
		return null
	}
	return { kind: 'count', subject: title, labels, state: state({ view: 'priceCurve', selection, digest, scope, definition }) }
}

/** Only Iris users get a Week-by-week cell detail, and only on a cell with a price (UX C9) */
export const weeklyCellOpenable = ({ isIris, cell }) => !!isIris && !!cell && cell.value !== null && cell.value !== undefined

const weekNumber = week => {
	const n = Number(String(week?.key ?? '').slice(4))
	return Number.isFinite(n) && n > 0 ? n : null
}

/**
 * A Week-by-week cell (`WeeklyTable`): `office` its office group, `row` the provider row, `date` the pick-up,
 * `week` the capture week header; `cell` as painted (`value`, `eur`, `pct`, `low`, `high`, `thin`, `noPrev`).
 */
export function weeklyCellEvidence({ office, row, date, week, cell, stat, lor, daily, code, carsText, marketText, scope }) {
	if (!cell || cell.value === null || cell.value === undefined || !row || !office || !week) return null
	const at = r => r.cells?.[date]?.[week.key]
	const priced = (office.rows || []).filter(r => r.kind !== 'median' && at(r) && at(r).value !== null)
		.map(r => compact({ provider: r.name, tag: r.tag || null, price: r2(at(r).value) }))
		.sort((a, b) => a.price - b.price)
	const median = (office.rows || []).find(r => r.kind === 'median')
	const you = (office.rows || []).find(r => r.kind === 'you')
	const n = weekNumber(week)
	const selection = compact({
		provider: row.name,
		market_median: row.kind === 'median' || null,
		office: office.label,
		pickup_date: date,
		week: compact({ label: n ? `Week ${n}` : week.label, from: week.from, to: week.to, in_progress: week.inProgress || null }),
		stat: STAT[stat] || null,
		length_days: lor ?? null,
		price: r2(cell.value),
		price_basis: daily ? 'daily' : 'total',
		currency: code || null,
		change: r2(cell.eur),
		change_pct: pct1(cell.pct),
		versus: week.first ? 'no previous week in the selection' : (week.versus || 'the week before'),
		no_previous_week: cell.noPrev || null,
		cheapest_of_shown: cell.low || null,
		dearest_of_shown: cell.high || null,
		few_prices: cell.thin || null,
	})
	const digest = compact({
		cheapest: priced[0] || null,
		dearest: priced.length > 1 ? priced[priced.length - 1] : null,
		market_median: median && at(median)?.value !== null ? r2(at(median)?.value) : null,
		your_row: you && at(you)?.value !== null && you !== row ? { provider: you.name, price: r2(at(you).value) } : null,
		providers_with_a_price: priced.length,
	})
	return {
		kind: 'cell',
		labels: [row.name, office.label, date ? { date } : null, n ? { week: n } : week.label, ...namedFilters({ carsText, marketText, scope })],
		state: state({ view: 'priceCurve', selection, digest, scope, definition: 'bi.weeklyCell' }),
	}
}

// ---------------------------------------------------------------------------
// My office network
// ---------------------------------------------------------------------------

const entry = e => compact({ name: e.name, price: r2(e.v) })

/**
 * The detail of a heat-map cell (`NetworkDetailDialog`), with its props. Brand (or website) mode asks about your
 * distance to the market; market mode (no identity) about the median's move. Your other brands go by name.
 */
export function networkCellEvidence({ office, column, cell, mode, code, perDay, compareText, carsText, marketText, scope }) {
	if (!office || !cell || cell.empty) return null
	const market = mode === 'market'
	const list = cell.list || []
	const medianAt = cell.median === null ? -1 : list.findIndex(e => e.v > cell.median)
	const selection = market
		? compact({
			office: office.name, column: column?.label, market_median: r2(cell.median), providers_in_median: cell.nCompetitors,
			change_pct: pct1(cell.marketChange), since: compareText || null, prices: cell.n, previous_prices: cell.prevN,
			currency: code || null, price_basis: perDay ? 'daily' : 'total', few_prices: cell.thin || null,
		})
		: compact({
			office: office.name, column: column?.label, me_as: mode === 'site' ? 'website' : 'brand',
			your_price: cell.you ? { name: cell.you.name, price: r2(cell.you.v) } : null,
			market_median: r2(cell.median), competitors_in_median: cell.nCompetitors, your_brands_excluded: true,
			distance_pct: pct1(cell.distance), distance: r2(cell.distanceE),
			change_points: pts1(cell.change), since: compareText || null,
			position: cell.position ? `${cell.position} of ${cell.total}` : null,
			prices: cell.n, your_prices: cell.you?.n ?? null, previous_prices: cell.prevN,
			currency: code || null, price_basis: perDay ? 'daily' : 'total', few_prices: cell.thin || null,
			no_value: cell.reason || null,
		})
	const digest = compact({
		cheapest: list.slice(0, 3).map(entry),
		dearest: list.length > 3 ? list.slice(-2).map(entry) : null,
		median_falls: medianAt > 0 ? `between ${list[medianAt - 1].name} and ${list[medianAt].name}` : null,
		your_other_brands: (cell.yours || []).map(e => e.name),
		in_the_cell: list.length,
	})
	return {
		kind: market ? 'marketMove' : 'distance',
		labels: [office.name, column?.label || null, ...namedFilters({ carsText, marketText, scope })],
		state: state({ view: 'network', selection, digest, scope, definition: market ? 'bi.networkMarket' : 'bi.networkCell' }),
	}
}

/**
 * A group summary row: `group` (`groupedView`: `label`, `rows`, `notCaptured`, `summary`), `columns` the labels of
 * its summary cells. Nothing to ask without groups or without an office with data.
 */
export function groupEvidence({ group, columns = [], mode, carsText, marketText, scope }) {
	if (!group || group.label === null || group.label === undefined) return null
	const summary = group.summary || []
	if (!summary.some(s => s.withData > 0)) return null
	const market = mode === 'market'
	const selection = compact({ group: group.label, offices: group.rows?.length ?? null, not_captured: group.notCaptured || null })
	const digest = compact({
		columns: summary.map((s, j) => (s.withData ? compact({
			column: columns[j]?.label ?? columns[j] ?? null,
			[market ? 'median_change_pct' : 'median_distance_pct']: pct1(s.median),
			...(market ? {} : { above_market: `${s.above} of ${s.withData}` }),
			with_data: s.withData,
		}) : null)).filter(Boolean).slice(0, MAX_GROUP_COLUMNS),
	})
	return {
		kind: 'group',
		labels: [group.label, { offices: group.rows?.length ?? null }, ...namedFilters({ carsText, marketText, scope })],
		state: state({ view: 'network', selection, digest, scope, definition: 'bi.group' }),
	}
}

/**
 * A ranking card (`Rankings`): its title, its entries as listed (office, column, value), since when. `card` =
 * `above` / `below` (distance) / `change` (points) / `market` (median change).
 */
export function rankingEvidence({ card, title, subtitle, entries = [], since, sinceText, scopeText, carsText, marketText, scope }) {
	if (!entries || !entries.length) return null
	const value = e => (card === 'change' ? { change_points: pts1(e.value) } : card === 'market' ? { median_change_pct: pct1(e.value) } : { distance_pct: pct1(e.value) })
	const selection = compact({ card: title, subtitle: subtitle || null, since: sinceText || null, over: scopeText || null })
	const digest = compact({ entries: entries.slice(0, MAX_ROWS).map(e => compact({ office: e.officeName, column: e.columnLabel, ...value(e) })) })
	return {
		kind: 'count',
		subject: title,
		labels: [since ? { since } : null, ...namedFilters({ carsText, marketText, scope })],
		state: state({ view: 'network', selection, digest, scope, definition: 'bi.ranking' }),
	}
}
